import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { isAiJobSettled, type ActiveAiTest } from '@kitobxonlik/contracts';
import { aiTestsApi } from '@/api';

/** Jarayon ketayotganda holat shu oraliqda yangilanadi (AI holati ham shunday tez-tez o'zgaradi) */
export const POLL_MS = 30_000;
/** Jarayon yo'q — boshqa admin yuborgan kitobni ham ko'rish uchun kamdan-kam */
export const IDLE_MS = 120_000;

/**
 * Fonda ketayotgan AI jarayonlari (2026-10-30, foydalanuvchi: «kitob AI'ga
 * yuborilganda jarayon fonda davom etaversin, boshqa sahifalarda ham
 * ko'rinib tursin»). Kitob AI'ga yuborilgach u AI'da o'zi ishlayveradi —
 * bu store faqat holatni kuzatadi (`AiJobsTray`).
 *
 * Ro'yxatdan chiqib ketgan jarayonning (savollar keldi, xato va h.k.)
 * yakuniy holati kitob bo'yicha alohida so'raladi — natija admin yopguncha
 * ko'rinib turadi.
 */
export const useAiTestsStore = defineStore('aiTests', () => {
  /** Kitob bo'yicha (bitta kitob — bitta qator): ketayotganlar va yopilmagan natijalar */
  const items = ref<ActiveAiTest[]>([]);
  let timer: ReturnType<typeof setTimeout> | null = null;
  let watching = false;
  let pending: Promise<void> | null = null;

  const running = computed(() => items.value.filter((item) => !isAiJobSettled(item.job)));

  function upsert(item: ActiveAiTest): void {
    const i = items.value.findIndex((x) => x.job.book_id === item.job.book_id);
    if (i === -1) items.value.push(item);
    else items.value[i] = item;
  }

  function dismiss(bookId: number): void {
    items.value = items.value.filter((item) => item.job.book_id !== bookId);
  }

  /** Bir vaqtda bitta so'rov — parallel chaqiruvlar o'shani kutadi */
  function load(): Promise<void> {
    pending ??= refresh().finally(() => {
      pending = null;
      schedule();
    });
    return pending;
  }

  async function refresh(): Promise<void> {
    try {
      const fresh = (await aiTestsApi.active()).items;
      const seen = new Set(fresh.map((item) => item.job.book_id));
      const gone = running.value.filter((item) => !seen.has(item.job.book_id));
      fresh.forEach(upsert);

      await Promise.all(
        gone.map(async (item) => {
          const bookId = item.job.book_id;
          try {
            const res = await aiTestsApi.latest(bookId);
            if (res.job) upsert({ ...item, job: res.job, refresh_error: res.refresh_error });
            else dismiss(bookId);
          } catch {
            // Kitob o'chirilgan va h.k. — kuzatishdan chiqariladi
            dismiss(bookId);
          }
        }),
      );
    } catch {
      // Tarmoq xatosi — keyingi so'rovda qayta
    }
  }

  function schedule(): void {
    if (timer) clearTimeout(timer);
    timer = null;
    if (watching) timer = setTimeout(() => void load(), running.value.length ? POLL_MS : IDLE_MS);
  }

  function start(): void {
    if (watching) return;
    watching = true;
    void load();
  }

  function stop(): void {
    watching = false;
    if (timer) clearTimeout(timer);
    timer = null;
  }

  function reset(): void {
    stop();
    items.value = [];
  }

  return { items, running, load, dismiss, start, stop, reset };
});
